import { Ban, CircleDashed, CircleHelp, SquareFunction, TrendingUp } from "lucide-react";
import type { Esito } from "@/api/preventivo";
import { GIUDIZIO } from "@/lib/analisiScritte";
import { conUnita, esitiConFrase, esitiGasVisibili } from "@/lib/preventivoFormato";

type Giudizio = keyof typeof GIUDIZIO;

// colori e icone delle pillole dell'analisi (ModaleBolletta): lo stesso esito si legge uguale in tutto il portale
const PILLOLA: Record<Giudizio, { icona: typeof Ban; classi: string }> = {
  G: { icona: TrendingUp, classi: "text-red-800 bg-red-50" },
  N: { icona: Ban, classi: "text-blue-900 bg-blue-50" },
  V: { icona: SquareFunction, classi: "text-blue-900 bg-blue-50" },
  P: { icona: CircleHelp, classi: "text-amber-800 bg-amber-50" },
  da_verificare: { icona: CircleDashed, classi: "text-slate-700 bg-slate-100" },
};

function noto(g: string): g is Giudizio {
  return g in GIUDIZIO;
}

/** La pillola dell'esito: icona e parola, coi colori dell'analisi. */
export function Pillola({ giudizio }: { giudizio: string }) {
  if (!noto(giudizio)) return null;
  const { icona: Icona, classi } = PILLOLA[giudizio];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold ${classi}`}>
      <Icona className="w-3 h-3 shrink-0" aria-hidden="true" />
      {GIUDIZIO[giudizio]}
    </span>
  );
}

/** «Addebiti in più» di un condominio: pillola e titolo, sotto la frase coi numeri (una per bolletta, con la fornitura
 *  davanti se il condominio ne ha più d'una). `className` dà le colonne. */
export function Esiti({ esiti, className = "", gas = false }: { esiti: Esito[]; className?: string; gas?: boolean }) {
  return (
    <ul className={`grid gap-x-6 gap-y-4 ${className}`}>
      {(gas ? esitiGasVisibili(esiti) : esitiConFrase(esiti)).map((e, i) => {
        return (
          <li key={`${e.giudizio}-${e.titolo}-${i}`} className="min-w-0 text-sm">
            <p className="flex flex-col items-start gap-1">
              <Pillola giudizio={e.giudizio} />
              <span className="min-w-0 font-semibold text-text break-words">{e.titolo}</span>
            </p>
            {e.voci.filter((v) => v.fornitura || v.frase).map((v, j) => (
              <p key={j} className="mt-1 max-w-[65ch] text-muted-foreground break-words">
                {v.fornitura && <span className="font-medium text-text">{v.fornitura}{v.frase ? ": " : ""}</span>}
                {conUnita(v.frase)}
              </p>
            ))}
          </li>
        );
      })}
    </ul>
  );
}
